import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Api, ApiError, type DomainStatus } from "../api";

/**
 * M1.5 — connect the business's own domain. One CNAME at their registrar;
 * mail and everything else stay untouched. Status flows
 * pending_dns → issuing → active; once active the Groundbeam subdomain
 * 301s to the domain path-preservingly.
 */
export function DomainCard({ api, entitled }: { api: Api; entitled: boolean }) {
  const [state, setState] = useState<DomainStatus | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.getDomain());
    } catch {
      // leave the loading state; retry on refresh
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const connect = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const domain = draft.trim().toLowerCase();
    if (!domain || busy) return;
    setBusy(true);
    setError(null);
    try {
      setState(await api.connectDomain(domain));
      setDraft("");
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not connect.",
      );
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setState(await api.disconnectDomain());
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not disconnect.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card domain-card">
      <div className="card-head">
        <div>
          <h2>Custom domain</h2>
          <p className="muted">
            Serve your site on your own domain. Email and everything else at
            your registrar stay exactly as they are.
          </p>
        </div>
        {state?.status === "active" && (
          <span className="site-status live">
            <span aria-hidden="true" className="status-dot" />
            Active
          </span>
        )}
      </div>

      {!entitled && (
        <p className="muted">
          Custom domains are available on the Growth plan.
        </p>
      )}

      {entitled && state && state.status === "none" && (
        <form className="domain-connect" onSubmit={connect}>
          <input
            type="text"
            placeholder="www.yourbusiness.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Your domain"
          />
          <button type="submit" disabled={busy || !draft.trim()}>
            {busy ? "Connecting…" : "Connect"}
          </button>
        </form>
      )}

      {entitled && state && state.status !== "none" && (
        <div className="domain-status">
          <p>
            <strong>{state.domain}</strong>
            {" — "}
            {state.status === "active"
              ? "live with HTTPS. Your Groundbeam address now redirects here."
              : state.status === "not_enabled"
                ? "queued — domain connections are being switched on for the platform; check back shortly."
                : state.status === "pending_dns"
                  ? "waiting for your DNS change."
                  : "DNS seen — issuing the security certificate (usually minutes)."}
          </p>
          {(state.status === "pending_dns" || state.status === "issuing") && (
            <div className="domain-instructions">
              <p className="muted small">
                At your domain registrar, add this record (nothing else
                changes):
              </p>
              <code>
                CNAME&nbsp;&nbsp;{state.domain}&nbsp;&nbsp;→&nbsp;&nbsp;
                {state.cnameTarget}
              </code>
              <p className="muted small">
                For a root domain (no www), use your registrar's ALIAS/ANAME
                record or web forwarding to the www version.
              </p>
            </div>
          )}
          {state.detail && <p className="muted small">{state.detail}</p>}
          <div className="domain-actions">
            {state.status !== "active" && (
              <button type="button" onClick={() => void load()} disabled={busy}>
                Check again
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void disconnect()}
              disabled={busy}
            >
              Disconnect
            </button>
          </div>
        </div>
      )}
      {error && <p className="notice">{error}</p>}
    </section>
  );
}
